import { useEffect } from 'react';

import type { Enrolment } from '@/lib/enrolmentSession';
import { ENROLMENT, useEnrolmentSession } from '@/lib/enrolmentSession';
import type { KioskSessionState } from '@/lib/kioskSession';
import { isCandidateSessionOpen, useKioskSession } from '@/lib/kioskSession';
import { reconnectDelayMs } from '@/lib/reconnectBackoff';
import type { CandidateSessionReport } from '@/services/liveSocket';
import { createLiveChannel } from '@/services/liveSocket';

/**
 * The enrolment states in which this tablet holds its credential [03-us-kad-6j-b § *Preconditions*]: every
 * state a successful connect or a boot verdict of *enrolled* can land on.
 *
 * ⛔ **Not `checking`, `pin`, `connecting` or `locked`.** In those the device holds no credential the api
 * would honour — or has not been told yet whether it does — and an upgrade could only be refused.
 *
 * ⚠️ **A tablet sent to the pad by `published: false` still holds a live credential** [03-us-kad-6e AC-3;
 * 0432], and it holds no socket: it is at the pad like any unenrolled device [0429 R1], so the admin reads
 * it as gone, and [KAD-6j] AC-5's `config.published` frame has no recipient on this device [KAD-6e §
 * *Known gaps at the seam*].
 */
const HOLDS_A_CREDENTIAL: ReadonlySet<Enrolment> = new Set([
  ENROLMENT.naming,
  ENROLMENT.ready,
  ENROLMENT.enrolled,
]);

/** The tab is in front: visible AND focused [AC-1, AC-3]. */
function inFront(): boolean {
  return document.visibilityState === 'visible' && document.hasFocus();
}

/**
 * The candidate session as the socket reports it [03-us-kad-6j-f]: open per {@link isCandidateSessionOpen},
 * with the latest candidate entered, or `null`.
 *
 * ⛔ The enrolment states are not a session: the candidate journey renders only once enrolled, and a `401`
 * clears the session before it takes the tablet to the pad.
 */
function candidateSessionOf(state: KioskSessionState): CandidateSessionReport | undefined {
  return isCandidateSessionOpen(state) ? { candidate: state.sessionCandidate } : undefined;
}

/**
 * ⭐ **The live status socket, held only while the tablet is enrolled AND in front** [03-us-kad-6j-b].
 *
 * * **Hide or blur closes it** [AC-2; 0354 § 3] — the close is the signal the admin reads as *needs
 *   attention*, and ⛔ nothing is sent to explain it.
 * * **Coming back opens a NEW one** [AC-3] — a fresh handshake, never a resumed one. A `visibilitychange`
 *   and a `focus` for the same return are one open: the channel ignores an open while it holds a socket.
 * * **A drop while in front is retried with backoff** by the channel itself [AC-3; 0354 § 5].
 * * **`config.published` is heard by nobody** [03-us-kad-6e; 0429 R1, which withdrew KAD-6e's former AC-4]:
 *   the *nothing published* state it recovered is gone, and with it the re-check every open made
 *   [decision 0365 A]. ⛔ Nothing on this device acts on a frame.
 * * **The browser's `online` restarts it** [decision 0365 B] — close and a fresh handshake,
 *   because after a NAT reboot or a WAN failover the server has dropped a socket the tablet still believes
 *   is open. ⛔ No timer. ⚠️ A path change the browser never reports is a known gap.
 *
 * * **It reports the candidate session** [03-us-kad-6j-f]: ONE subscription to the kiosk store, so a
 *   session that starts, gets a name, or ends — by any of the paths back to Welcome — is told to the
 *   channel, which sends only while a socket is open and re-sends on every open. ⛔ No timer sends a frame.
 *
 * ⛔ **These listeners are NOT the boot recovery's** [`App` — `recheckEnrolment`, KAD-6i AC-10]. That one
 * listens to `visibilitychange` and `online` and probes only from the pad; this one holds a socket only in
 * the states that one never runs in, and ⛔ it makes no HTTP request at all. One show is still one probe.
 */
export function useLiveStatusSocket(): void {
  const holdsCredential = useEnrolmentSession((state) => HOLDS_A_CREDENTIAL.has(state.enrolment));

  useEffect(() => {
    if (!holdsCredential) return;

    // ⭐ A NEW channel at every load and every return from the pad, so each starts with an end owed: its first
    // successful open sends `session.ended` first [03-us-kad-6j-h AC-2].
    const channel = createLiveChannel({});
    // Told before the first open, so that open reports a session already under way [AC-3].
    channel.setCandidateSession(candidateSessionOf(useKioskSession.getState()));
    const stopReporting = useKioskSession.subscribe((state) => {
      channel.setCandidateSession(candidateSessionOf(state));
    });
    const follow = (): void => {
      if (inFront()) channel.open();
      else channel.close();
    };
    /*
     * ⭐ **Jittered, once** [decision 0365 B; PR 224 review 2 item 2]. `online` reaches every tablet behind a
     * branch address in the same instant — the reason the boot recovery jitters its own `online`
     * [`enrolmentSession.ts`, `RecoveryTrigger`] — so the reopen waits a draw of `reconnectDelayMs(0)`
     * (0–1 s). A burst of `online` events inside that wait is still ONE reopen. ⛔ Not a timer that repeats.
     */
    let pendingRestart: ReturnType<typeof setTimeout> | undefined;
    const restart = (): void => {
      channel.close();
      clearTimeout(pendingRestart);
      pendingRestart = setTimeout(() => {
        pendingRestart = undefined;
        follow();
      }, reconnectDelayMs(0));
    };

    follow();
    document.addEventListener('visibilitychange', follow);
    window.addEventListener('focus', follow);
    window.addEventListener('blur', channel.close);
    window.addEventListener('online', restart);
    return () => {
      stopReporting();
      document.removeEventListener('visibilitychange', follow);
      window.removeEventListener('focus', follow);
      window.removeEventListener('blur', channel.close);
      window.removeEventListener('online', restart);
      clearTimeout(pendingRestart);
      // Leaving the enrolled states — a refused credential sent the tablet back to the pad — ends the channel.
      channel.close();
    };
  }, [holdsCredential]);
}
